import ReactECharts from 'echarts-for-react';

/**
 * AuditTrailChart — documented AI incidents per year (blog04).
 * Component name kept for import compatibility; the content is now the real
 * AI Incident Database curve as reported by the Stanford HAI AI Index.
 *
 * Data (mirrors public/blog/ai-incidents-by-year.csv):
 *   2023: 123 — AI Index Report 2024, ch.3: "123 incidents were reported in 2023"
 *     https://hai.stanford.edu/assets/files/hai_ai-index-report-2024_chapter3.pdf
 *   2024: 233 — AI Index Report 2025, ch.3: "rose to 233 in 2024 — a record high
 *     and a 56.4% increase over 2023"
 *     https://hai.stanford.edu/assets/files/hai_ai_index_report_2025.pdf
 *   2025: 362 — AI Index Report 2026, ch.3: "362, up from 233 in 2024"; incident
 *     counts "stayed under 100 until 2022"
 *     https://hai.stanford.edu/assets/files/ai_index_report_2026.pdf
 *
 * Chart-type-first: comparison of a count across years → ECharts native bar,
 * with a dashed context markLine at 100. Brand colors; opaque white background.
 */

const BRAND = {
  teal: '#0AB3BF',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const YEARS = ['2023', '2024', '2025'];
// Documented AI incidents per year — Stanford HAI AI Index 2024/2025/2026 (see header).
const INCIDENTS = [123, 233, 362];

/** ONE shared count formatter — tooltip, axis ticks, and bar labels all use it. */
const formatCount = (v: number) => `${v}`;

export const AuditTrailChart = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { value: number; dataIndex: number }[]) =>
        `${YEARS[params[0].dataIndex]}<br/>documented AI incidents: <b>${formatCount(params[0].value)}</b>`,
    },
    grid: { left: 48, right: 24, top: 48, bottom: 36 },
    xAxis: {
      type: 'category',
      data: YEARS,
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.muted, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'documented incidents',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatCount(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    series: [
      {
        name: 'Documented AI incidents',
        type: 'bar',
        barWidth: '42%',
        itemStyle: { color: BRAND.teal, borderRadius: [4, 4, 0, 0] },
        data: INCIDENTS,
        label: {
          show: true,
          position: 'top',
          color: BRAND.slate,
          fontSize: 13,
          fontWeight: 700,
          formatter: (p: { value: number }) => formatCount(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: BRAND.muted, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: BRAND.muted,
            fontSize: 11,
            formatter: 'under 100 every year until 2022',
          },
          data: [{ yAxis: 100 }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. Documented AI incidents nearly tripled in two years (123 → 362). Every incident on this curve is an
        event where someone needed a receipt. Source: Stanford HAI AI Index 2024–2026.
      </p>
      <p className="mt-1 text-label-xs text-slate-600 dark:text-slate-500 text-center">
        AI Incident Database backfills historical years; per-year totals can be revised upward (AI Index 2026 note).
      </p>
    </div>
  );
};
